import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { usePublicChampionship } from "@/contexts/PublicChampionshipContext";
import { useEffect, useRef } from "react";

type BannerPosition = "banner_top" | "banner_mid" | "banner_bottom";

function useAdminAds() {
  return useQuery({
    queryKey: ["admin-ads-public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_ads")
        .select("*")
        .eq("active", true)
        .order("sort_order");
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function usePublicBanners() {
  const { championshipId: id, championship } = usePublicChampionship();
  const isPaid = championship?.plan && championship.plan !== "start";

  const { data: championshipBanners = [] } = useQuery({
    queryKey: ["public-banners", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("championship_sponsors")
        .select("*")
        .eq("championship_id", id!)
        .in("type", ["banner_top", "banner_mid", "banner_bottom"])
        .eq("active", true);
      if (error) throw error;
      return data;
    },
    enabled: !!id && !!isPaid,
  });

  const { data: adminAds = [] } = useAdminAds();

  const getBanner = (position: BannerPosition) => {
    if (isPaid) {
      return { type: "championship" as const, data: championshipBanners.find((b) => b.type === position) };
    }
    const adminAd = adminAds.find((a) => a.slot === position);
    if (adminAd) {
      return { type: "admin" as const, data: adminAd };
    }
    return null;
  };

  return { getBanner, isPaid, adminAds };
}

const BANNER_MAX: Record<BannerPosition, { maxWidth: number; maxHeight: number }> = {
  banner_top: { maxWidth: 728, maxHeight: 90 },
  banner_mid: { maxWidth: 970, maxHeight: 250 },
  banner_bottom: { maxWidth: 728, maxHeight: 90 },
};

function AdsenseSlot({ code }: { code: string }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.innerHTML = code;
    // Execute any scripts in the adsense code
    const scripts = containerRef.current.querySelectorAll("script");
    scripts.forEach((script) => {
      const newScript = document.createElement("script");
      Array.from(script.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));
      newScript.textContent = script.textContent;
      script.parentNode?.replaceChild(newScript, script);
    });
  }, [code]);

  return <div ref={containerRef} className="w-full flex justify-center" />;
}

export function PublicBanner({ position }: { position: BannerPosition }) {
  const { t } = useTranslation("pages");
  const { getBanner } = usePublicBanners();
  const result = getBanner(position);

  if (!result || !result.data) return null;

  const spec = BANNER_MAX[position];

  if (result.type === "admin") {
    const ad = result.data as any;
    if (ad.ad_type === "adsense" && ad.adsense_code) {
      return (
        <div className="w-full flex justify-center px-4">
          <AdsenseSlot code={ad.adsense_code} />
        </div>
      );
    }
    if (ad.image_url) {
      return (
        <div className="w-full flex justify-center px-4">
          <img
            src={ad.image_url}
            alt={ad.name || t("publicLayout.bannerAlt")}
            className="rounded-lg object-contain w-full"
            style={{ maxWidth: spec.maxWidth, maxHeight: spec.maxHeight }}
            loading="lazy"
          />
        </div>
      );
    }
    return null;
  }

  // Championship banner (paid plan)
  const banner = result.data as any;
  return (
    <div className="w-full flex justify-center px-4">
      <img
        src={banner.image_url}
        alt={banner.name || t("publicLayout.bannerAlt")}
        className="rounded-lg object-contain w-full"
        style={{ maxWidth: spec.maxWidth, maxHeight: spec.maxHeight }}
        loading="lazy"
      />
    </div>
  );
}

export function PublicApoiadores() {
  const { t } = useTranslation("pages");
  const { championshipId: id, championship } = usePublicChampionship();
  const isPaid = championship?.plan && championship.plan !== "start";

  const { data: championshipApoiadores = [] } = useQuery({
    queryKey: ["public-apoiadores", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("championship_sponsors")
        .select("*")
        .eq("championship_id", id!)
        .eq("type", "apoiador")
        .eq("active", true)
        .order("sort_order");
      if (error) throw error;
      return data;
    },
    enabled: !!id && !!isPaid,
  });

  const { data: adminAds = [] } = useAdminAds();
  const adminApoiadores = adminAds.filter((a) => a.slot === "apoiador");

  const items = isPaid ? championshipApoiadores : adminApoiadores;

  if (items.length === 0) return null;

  const count = items.length;
  const maxPx = count <= 1 ? 120 : count <= 3 ? 100 : 80;

  return (
    <div className="mt-8 pt-6 border-t border-border">
      <p className="text-xs font-semibold text-muted-foreground text-center mb-4">{t("publicLayout.supporters")}</p>
      <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
        {items.map((a: any) => {
          if (a.ad_type === "adsense" && a.adsense_code) {
            return (
              <div key={a.id} style={{ width: maxPx, height: maxPx, maxWidth: "20vw", maxHeight: "20vw" }}>
                <AdsenseSlot code={a.adsense_code} />
              </div>
            );
          }
          const imgUrl = a.image_url;
          if (!imgUrl) return null;
          return (
            <div
              key={a.id}
              className="rounded-lg overflow-hidden"
              style={{ width: maxPx, height: maxPx, maxWidth: "20vw", maxHeight: "20vw" }}
            >
              <img
                src={imgUrl}
                alt={a.name || t("publicLayout.supporterAlt")}
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
